if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onStoreSelected = this.onStoreSelected.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) rootUrl += '/';

        fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch(() => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.available) {
          this.dataset.variantId = variant.id;
          this.fetchAvailability(variant.id);
        } else {
          document.querySelector('pickup-availability-drawer')?.remove();
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);
        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        document.removeEventListener('pickup:store-selected', this.onStoreSelected);

        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();

        const preview = sectionInnerHTML?.querySelector('pickup-availability-preview');
        const nextDrawer = sectionInnerHTML?.querySelector('pickup-availability-drawer');
        if (!preview || !nextDrawer) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');
        document.body.appendChild(nextDrawer);

        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => nextDrawer.classList.add(colorClass));

        document.addEventListener('pickup:store-selected', this.onStoreSelected);
        this.bindPreviewEvents(nextDrawer);
        this.restoreSelectedStore(nextDrawer);
      }

      bindPreviewEvents(drawer) {
        this.querySelector('#ShowPickupAvailabilityDrawer')?.addEventListener('click', (event) => {
          drawer.show(event.currentTarget);
        });

        this.querySelector('.pickup-availability-order-pick')?.addEventListener('click', () => {
          this.handleOrderAndPick();
        });

        const authDialog = this.querySelector('.pickup-availability-auth-dialog');
        authDialog?.querySelectorAll('[data-pickup-auth-close]').forEach((button) => {
          button.addEventListener('click', () => authDialog.close());
        });
      }

      restoreSelectedStore(drawer) {
        let storedLocation;
        try {
          storedLocation = JSON.parse(sessionStorage.getItem('rwhPickupLocation'));
        } catch (_error) {
          storedLocation = null;
        }

        if (!storedLocation?.id) return;
        const location = Array.from(drawer.querySelectorAll('[data-pickup-location]')).find(
          (item) => item.dataset.locationId === storedLocation.id && item.dataset.locationAvailable === 'true'
        );
        if (location) this.applySelectedStore(location.dataset);
      }

      onStoreSelected(event) {
        this.applySelectedStore(event.detail);
      }

      applySelectedStore(location) {
        const selectedStore = this.querySelector('[data-selected-pickup-store]');
        if (!selectedStore) return;

        selectedStore.dataset.locationId = location.locationId;
        selectedStore.querySelector('[data-selected-location-name]').textContent = location.locationName;
        selectedStore.querySelector('[data-selected-location-status]').textContent = 'In stock';
        selectedStore.querySelector('[data-selected-location-ready]').textContent = location.locationReady;

        const hours = selectedStore.querySelector('[data-selected-location-hours]');
        if (hours) hours.textContent = location.locationHours || '';

        sessionStorage.setItem(
          'rwhPickupLocation',
          JSON.stringify({ id: location.locationId, name: location.locationName })
        );
      }

      async handleOrderAndPick() {
        const preview = this.querySelector('pickup-availability-preview');
        const selectedStore = this.querySelector('[data-selected-pickup-store]');
        const orderButton = this.querySelector('.pickup-availability-order-pick');
        if (!preview || !selectedStore || !orderButton) return;

        if (preview.dataset.customerAuthenticated !== 'true') {
          const dialog = preview.querySelector('.pickup-availability-auth-dialog');
          if (dialog?.showModal) dialog.showModal();
          else dialog?.setAttribute('open', '');
          return;
        }

        orderButton.disabled = true;
        orderButton.setAttribute('aria-busy', 'true');

        try {
          const variantId = Number(preview.dataset.variantId || this.dataset.variantId);
          const locationId = selectedStore.dataset.locationId;
          const locationName = selectedStore.querySelector('[data-selected-location-name]')?.textContent.trim();
          const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;

          const addResponse = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                  properties: {
                    '_Click & Collect location': locationName,
                    '_Click & Collect location ID': locationId,
                  },
                },
              ],
            }),
          });
          if (!addResponse.ok) throw new Error('Unable to add the selected item.');

          await fetch(`${rootUrl}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              attributes: {
                'Click & Collect location': locationName,
                'Click & Collect location ID': locationId,
              },
            }),
          });

          window.location.assign(`${rootUrl}checkout`);
        } catch (_error) {
          orderButton.disabled = false;
          orderButton.removeAttribute('aria-busy');
          orderButton.textContent = 'PLEASE TRY AGAIN';
        }
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.querySelector('.pickup-availability-drawer-button')?.addEventListener('click', () => this.hide());
        this.querySelectorAll('.pickup-availability-store-option').forEach((button) => {
          button.addEventListener('click', () => this.selectStore(button.closest('[data-pickup-location]')));
        });
        this.querySelector('[data-use-current-location]')?.addEventListener('click', () => this.useCurrentLocation());

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(event) {
        const target = event.target;
        if (target !== this && !target.closest('pickup-availability-drawer') && target.id !== 'ShowPickupAvailabilityDrawer') {
          this.hide();
        }
      }

      hide() {
        this.motionGeneration = (this.motionGeneration || 0) + 1;
        window.clearTimeout(this.motionTimer);
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        if (this.focusElement) removeTrapFocus(this.focusElement);
        this.motionTimer = window.setTimeout(() => {
          document.body.classList.remove('overflow-hidden');
        }, 650);
      }

      show(focusElement) {
        window.clearTimeout(this.motionTimer);
        const generation = this.motionGeneration = (this.motionGeneration || 0) + 1;
        this.focusElement = focusElement;
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        // Paint the closed position first, as in the approved Styling Guide drawer.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.isConnected || generation !== this.motionGeneration) return;
          this.setAttribute('open', '');
          this.motionTimer = window.setTimeout(() => {
            if (this.hasAttribute('open')) trapFocus(this);
          }, 600);
        }));
      }

      selectStore(location) {
        if (!location || location.dataset.locationAvailable !== 'true') return;
        document.dispatchEvent(new CustomEvent('pickup:store-selected', { detail: { ...location.dataset } }));
        this.hide();
      }

      useCurrentLocation() {
        const message = this.querySelector('[data-location-message]');
        if (!navigator.geolocation) {
          message.textContent = 'Location is not supported by this browser. Please select a store manually.';
          return;
        }

        message.textContent = 'Requesting your location…';
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            const locations = Array.from(this.querySelectorAll('[data-pickup-location]'));
            let positionedLocations = 0;

            locations.forEach((location) => {
              const latitude = Number(location.dataset.locationLatitude);
              const longitude = Number(location.dataset.locationLongitude);
              if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

              const distance = this.distanceInKilometres(coords.latitude, coords.longitude, latitude, longitude);
              location.dataset.distance = distance;
              const distanceLabel = location.querySelector('[data-location-distance]');
              if (distanceLabel) distanceLabel.textContent = `${distance.toFixed(1)} km away`;
              positionedLocations += 1;
            });

            if (positionedLocations) {
              const list = this.querySelector('[data-store-availability-drawer-content]');
              locations
                .sort((a, b) => Number(a.dataset.distance || Infinity) - Number(b.dataset.distance || Infinity))
                .forEach((location) => list.appendChild(location));
              message.textContent = 'Stores are sorted by distance.';
            } else {
              message.textContent = 'Store distance is unavailable. Please select a store manually.';
            }
          },
          () => {
            message.textContent = 'Location access was not allowed. Please select a store manually.';
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      }

      distanceInKilometres(latitude1, longitude1, latitude2, longitude2) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const earthRadius = 6371;
        const latitudeDelta = toRadians(latitude2 - latitude1);
        const longitudeDelta = toRadians(longitude2 - longitude1);
        const value =
          Math.sin(latitudeDelta / 2) ** 2 +
          Math.cos(toRadians(latitude1)) *
            Math.cos(toRadians(latitude2)) *
            Math.sin(longitudeDelta / 2) ** 2;
        return earthRadius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
      }
    }
  );
}
